import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Avatar,
  Button,
  Link as MuiLink,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import {
  followUser,
  unfollowUser,
  isFollowingUser,
  fetchFollowersCount,
  fetchFollowingCount,
} from "../../../services/profileService";

const ProfileHeader = ({ userProfile, currentUser, theme }) => {
  const [isFollowing, setIsFollowing] = useState(false);
  const [followersCount, setFollowersCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);
  const isCurrentUser = currentUser?.uid === userProfile?.uid;
  const navigate = useNavigate();

  // Fetch following status, followers count, and following count
  useEffect(() => {
    const fetchData = async () => {
      if (!isCurrentUser && currentUser && userProfile) {
        try {
          const followingStatus = await isFollowingUser(
            currentUser, // ✅ Correctly pass the user object
            userProfile.uid
          );
          setIsFollowing(followingStatus);
        } catch (error) {
          console.error("Error fetching follow status:", error);
        }
      }

      if (userProfile) {
        try {
          const followers = await fetchFollowersCount(userProfile.uid);
          const following = await fetchFollowingCount(userProfile.uid);
          setFollowersCount(followers);
          setFollowingCount(following);
        } catch (error) {
          console.error("Error fetching counts:", error);
        }
      }
    };

    fetchData();
  }, [userProfile, currentUser, isCurrentUser]);

  const handleFollowToggle = async () => {
    // Check if the user is authenticated
    if (!currentUser) {
      // If not authenticated, prompt them to log in
      alert("You need to be logged in to follow users.");
      navigate("/challenge/signin"); // Navigate to the signin page
      return;
    }

    try {
      if (isFollowing) {
        await unfollowUser(currentUser, userProfile);
        setIsFollowing(false);
        setFollowersCount((prev) => prev - 1);
      } else {
        await followUser(currentUser, userProfile);
        setIsFollowing(true);
        setFollowersCount((prev) => prev + 1);
      }
    } catch (error) {
      console.error("Error toggling follow status:", error);
    }
  };

  // Extract the first name from the display name
  const displayName = userProfile?.displayName || "Anonymous";
  const firstName = displayName.split(" ")[0];

  return (
    <Box>
      {/* First Row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
          flexDirection: { xs: "column", sm: "row" }, // Stack vertically on small screens
        }}
      >
        {/* First Column: User Avatar */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flex: "0 0 auto",
            marginRight: { xs: 0, sm: 2 },
            mb: { xs: 2, sm: 0 }, // Add margin bottom on small screens
          }}
        >
          <Avatar
            src={userProfile?.profileImage}
            alt={userProfile?.displayName}
            sx={{ width: 80, height: 80, margin: "15px 15px 15px 0" }}
          />
        </Box>

        {/* Second Column: User Display Name and Stats */}
        <Box
          sx={{
            flex: 1,
            textAlign: {
              xs: "center",
              sm: "center",
              md: "left",
              lg: "left",
              xl: "left",
            },
            fontFamily: "basic-sans",
            width: "100%",
          }}
        >
          <Typography
            variant="h4"
            component="h2"
            gutterBottom
            sx={{ fontFamily: "basic-sans", fontWeight: "700" }}
          >
            {displayName}
          </Typography>
          <Typography
            variant="body1"
            color={theme.colors.darkGray}
            sx={{ fontFamily: "basic-sans" }}
          >
            Following: {followingCount} Followers: {followersCount}
          </Typography>

          {/* Stack Edit Profile and View Contributions links on mobile */}
          {isCurrentUser && (
            <Box
              sx={{
                mt: { xs: 1, sm: 2 }, // Add margin top on small screens
                display: "flex",
                flexDirection: { xs: "column", sm: "row" }, // Stack vertically on small screens
                gap: 1, // Add space between links
              }}
            >
              <MuiLink
                component={Link}
                to="/challenge/profile/edit"
                sx={{
                  color: theme.colors.amber,
                  fontFamily: "basic-sans",
                  textDecoration: "none",
                  fontSize: "1rem",
                }}
              >
                Edit My Profile
              </MuiLink>
              <Typography
                variant="body1"
                color={theme.colors.darkGray}
                sx={{
                  fontFamily: "basic-sans",
                  fontWeight: "700",
                  display: {
                    xs: "none",
                    sm: "block",
                    md: "block",
                    lg: "block",
                    xl: "block", // Fixed typo here
                  },
                }}
              >
                {" "}
                |{" "}
              </Typography>
              <MuiLink
                component={Link}
                to="/challenge/submission/contributions"
                sx={{
                  color: theme.colors.amber,
                  fontFamily: "basic-sans",
                  textDecoration: "none",
                  fontSize: "1rem",
                }}
              >
                View My Purchases
              </MuiLink>
            </Box>
          )}
        </Box>
      </Box>

      {/* Second Row: User Bio */}
      <Box
        sx={{
          width: { xs: "100%", sm: "100%", md: "50%" },
          mt: 2,
          fontFamily: "basic-sans",
        }}
      >
        <Typography
          variant="body1"
          sx={{
            color: theme.colors.darkGray,
            fontFamily: "basic-sans",
          }}
        >
          {userProfile?.bio || "No bio available."}
        </Typography>
      </Box>

      {/* Third Row: Follow Button */}
      {!isCurrentUser && currentUser && (
        <Box sx={{ mt: 2 }}>
          <Button
            onClick={handleFollowToggle}
            sx={{
              color: "white",
              fontWeight: "700",
              backgroundColor: isFollowing
                ? theme.colors.red
                : theme.colors.green,
              borderRadius: "50px",
              textTransform: "none",
              padding: "10px 20px",
              "&:hover": {
                backgroundColor: isFollowing
                  ? theme.colors.amber
                  : theme.colors.blue,
              },
              boxShadow: "none",
            }}
          >
            {isFollowing ? `Unfollow ${firstName}` : `Follow ${firstName}`}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default ProfileHeader;
